import useSWR from 'swr';
import { useMemo, useState, useCallback } from 'react';
import useSWRMutation from 'swr/mutation';
import tairraAxiosInstance, {
  fetcher,
  postFetcher,
  fetchGetRequest,
  handlePostRequestWithArg,
  handleDeleteRequestWithArg,
  handleGetRequestWithArg,
  handlePutRequestWithArg,
} from 'src/utils/axios-tairra';
import { toast } from 'src/components/snackbar';
import { apiEndpoints } from 'src/utils/api-endpoints/index';
import { mutate } from 'swr';

const swrOptions = {
  revalidateIfStale: true,
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  revalidateOnMount: true,
  dedupingInterval: 5000,
};

// export function useGetAllSettings(shouldFetch: boolean) {
//   const { data, error, mutate } = useSWR(
//     shouldFetch ? `${apiEndpoints.setup.getAllSettings}` : null,
//     handleGetRequestWithArg,
//     { revalidateOnFocus: false }
//   );

//   const fetchAllSettings = async () => {
//     try {
//       const response = await handleGetRequestWithArg(`${apiEndpoints.setup.getAllSettings}`, {});
//       mutate(response, false);
//       return response;
//     } catch (err) {
//       console.error('Error fetching settings:', err);
//       return [];
//     }
//   };

//   return {
//     settingsData: data,
//     isSettingsLoading: !data && !error,
//     settingsError: error,
//     fetchAllSettings,
//   };
// }

export function useGetAllSettings() {
  const GET_SETTINGS = `${apiEndpoints.setup.getAllSettings}`;

  const { data, error, isLoading, isValidating } = useSWR(GET_SETTINGS, fetcher);

  const memoizedValue = useMemo(() => {
    const settingsData = data?.data ?? null;
    const hasError = Boolean(error);

    return {
      settingsData,
      settingsDataLoading: isLoading,
      settingsDataError: hasError ? error : null,
      settingsDataValidating: isValidating,
      settingsDataNotFound: !isLoading && !settingsData,
    };
  }, [data?.data, error, isLoading, isValidating]);

  return memoizedValue;
}

// export function useGetClientDetails() {
//   const { data, error } = useSWR(
//     (id: number) => id ? `${apiEndpoints.setup.getClientDetail}/${id}` : null,
//     handleGetRequestWithArg
//   );

//   const clientDetails = async (id: number) => {
//     if (id === undefined || id === null) {
//       console.error('Invalid ID:', id);
//       return [];
//     }
//     try {
//       const response = await handleGetRequestWithArg(`${apiEndpoints.setup.getClientDetail}/${id}`, {});
//       const result = await response;
//       ;
//       return result;
//     } catch (err) {
//       console.error('Error fetching Client details:', err);
//       return [];
//     }
//   };

//   return {
//     clientDetails,
//     isClientDetailsLoading: !data && !error,
//     clientDetailsError: error,
//   };
// }

export function useGetClientDetails(id: number) {
  const { data, error, isValidating } = useSWR(
    id !== undefined ? `${apiEndpoints.setup.getClientDetail}/${id}` : null,
    fetcher
  );

  const memoizedClientDetail = useMemo(() => {
    const clientDetail = data?.data ?? [];

    const hasError = Boolean(error);

    return {
      clientDetail,

      clientDetailLoading: !data && !error,
      clientDetailError: hasError ? error : null,
      clientDetailValidating: isValidating,
      clientDetailNotFound: !data?.data?.length && !error,
      mutateClientDetail: () => mutate([apiEndpoints.setup.getClientDetail]),
    };
  }, [data?.data, data?.total, error, isValidating, id]);

  return memoizedClientDetail;
}
export function useGetLevelDataStatus() {
  const { data, error: fetchError } = useSWR(
    `${apiEndpoints.setup.getLevelDataStatus}`,
    handleGetRequestWithArg,
    swrOptions
  );

  const fetchLevelDataStatus = async () => {
    try {
      const response = await handleGetRequestWithArg(
        `${apiEndpoints.setup.getLevelDataStatus}`,
        {}
      );
      const result = await response;
      // console.log('Level Data status:', result);
      return result;
    } catch (err) {
      console.error('Error fetching level data status:', err);
      return [];
    }
  };

  return {
    fetchLevelDataStatus,
    isLevelDataStatusLoading: !data && !fetchError,
    levelDataStatusError: fetchError,
  };
}
export function useGetAllLevels() {
  // Fetch data with SWR
  const { data, error, isLoading } = useSWR(
    `${apiEndpoints.setup.getAllLevels}`,
    (url) => handleGetRequestWithArg(url, {}),
    swrOptions
  );

  // Optional manual fetch function (to refresh or use outside of SWR)
  const fetchAllLevels = async () => {
    try {
      const response = await handleGetRequestWithArg(`${apiEndpoints.setup.getAllLevels}`, {});
      const result = await response;

      // console.log('API Response:', result);

      if (result.returnStatus) {
        // console.log('All Levels Data:', result.data);
        return result.data;
      } else {
        console.error('Failed to fetch levels:', result.returnMessage);
        return [];
      }
    } catch (err) {
      console.error('Error fetching all levels:', err);
      return [];
    }
  };

  return {
    allLevels: data?.data || [],
    isAllLevelsLoading: isLoading,
    allLevelsStatusError: error,
    fetchAllLevels,
  };
}
export function useGetExternalLinks(shouldFetch: boolean) {
  const { data, error, mutate } = useSWR(
    shouldFetch ? `${apiEndpoints.setup.getAllExternalLinks}` : null,
    handleGetRequestWithArg,
    swrOptions
  );

  const fetchExternalLinks = async () => {
    try {
      const response = await handleGetRequestWithArg(
        `${apiEndpoints.setup.getAllExternalLinks}`,
        {}
      );
      mutate(response, false);
      return response;
    } catch (err) {
      console.error('Error fetching external links:', err);
      return [];
    }
  };

  return {
    externalLinksData: data,
    isExternalLinksLoading: !data && !error,
    externalLinksError: error,
    fetchExternalLinks,
  };
}
export function useGetRejectReasons(shouldFetch: boolean) {
  const { data, error, mutate } = useSWR(
    shouldFetch ? `${apiEndpoints.setup.getAllRejectReasons}` : null,
    handleGetRequestWithArg,
    swrOptions
  );

  const fetchRejectReasons = async () => {
    try {
      const response = await handleGetRequestWithArg(
        `${apiEndpoints.setup.getAllRejectReasons}`,
        {}
      );
      mutate(response, false);
      return response;
    } catch (err) {
      console.error('Error fetching reject reason:', err);
      return [];
    }
  };

  return {
    rejectedReasonsData: data,
    isrejectReasonLoading: !data && !error,
    rejectReasonError: error,
    fetchRejectReasons,
  };
}
export function useGetEmployeeStatus(shouldFetch: boolean) {
  const { data, error, mutate } = useSWR(
    shouldFetch ? `${apiEndpoints.setup.getAllEmployeeStatus}` : null,
    handleGetRequestWithArg,
    swrOptions
  );

  const fetchEmployeeStatus = async () => {
    try {
      const response = await handleGetRequestWithArg(
        `${apiEndpoints.setup.getAllEmployeeStatus}`,
        {}
      );
      mutate(response, false);
      return response;
    } catch (err) {
      console.error('Error fetching employee status:', err);

      return [];
    }
  };

  return {
    employeeStatusData: data,
    isEmployeeStatusLoading: !data && !error,
    employeeStatusError: error,
    fetchEmployeeStatus,
  };
}
export function useGetSaleStatus(shouldFetch: boolean) {
  const { data, error, mutate } = useSWR(
    shouldFetch ? `${apiEndpoints.setup.getAllSaleStatus}` : null,
    handleGetRequestWithArg,
    swrOptions
  );
  const fetchSaleStatus = async () => {
    try {
      const response = await handleGetRequestWithArg(`${apiEndpoints.setup.getAllSaleStatus}`, {});
      mutate(response, false);
      return response;
    } catch (err) {
      console.error('Error fetching sale status:', err);

      return [];
    }
  };

  return {
    saleStatusData: data,
    isSaleStatusLoading: !data && !error,
    saleStatusError: error,
    fetchSaleStatus,
  };
}
export function useGetContracts(shouldFetch: boolean) {
  const { data, error, mutate } = useSWR(
    shouldFetch ? `${apiEndpoints.setup.getAllContracts}` : null,
    handleGetRequestWithArg,
    swrOptions
  );

  const fetchContracts = async () => {
    try {
      const response = await handleGetRequestWithArg(`${apiEndpoints.setup.getAllContracts}`, {});
      mutate(response, false);
      return response;
    } catch (err) {
      console.error('Error fetching contracts:', err);

      return [];
    }
  };

  return {
    contractsData: data,
    isContractLoading: !data && !error,
    contractError: error,
    fetchContracts,
  };
}
export function useDesignationFilter(page: number, pageSize: number) {
  const requestData = {
    page,
    pageSize,
  };

  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.setup.getDesignationFilter, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const designationList = data?.data ?? [];
    const total = data?.total ?? 0;

    return {
      designationFilter: { designationList, total },
      designationLoading: isLoading,
      designationError: error,
      designationValidating: isValidating,
      designationEmpty: !isLoading && !designationList.length,
      mutateDesignation: () => mutate([apiEndpoints.setup.getDesignationFilter, requestData]),
    };
  }, [data, error, isValidating, page, pageSize]);

  return memoizedValue;
}
export function useProjectTypeFilter(page: number, pageSize: number) {
  const requestData = {
    page,
    pageSize,
  };

  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.setup.getProjectTypeFilter, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const projecTypeLists = data?.data ?? [];
    const total = data?.total ?? 0;

    return {
      projectTypes: { projecTypeLists, total },
      projectTypesnLoading: isLoading,
      projectTypeError: error,
      projcetTypeValidating: isValidating,
      projectTypeEmpty: !isLoading && !projecTypeLists.length,
      mutateProjectGoals: () => mutate([apiEndpoints.setup.getProjectTypeFilter, requestData]),
    };
  }, [data, error, isValidating, page, pageSize]);

  return memoizedValue;
}
export function useAddRejectReason() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addRejectReason,
    handlePostRequestWithArg
  );

  const addRejectReason = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addRejectReason,
    isLoading: isMutating,
    error,
  };
}
export function useDeleteRejectReason() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteRejectReason}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteRejectReason = async (id: number) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteRejectReason}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete reject reason';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Reject Reason deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Error deleting reject reason:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while deleting the reject reason'
      );
      return false;
    }
  };

  return {
    deleteRejectReason,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateRejectReason() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateRejectReason}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateRejectReason = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateRejectReason}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating employee:', err);
      return [];
    }
  };

  return {
    updateRejectReason,
    isLoading: !data && !error,
    error,
  };
}
export function useAddSaleStatus() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addSaleStatus,
    handlePostRequestWithArg
  );

  const addSaleStatus = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addSaleStatus,
    isLoading: isMutating,
    error,
  };
}
export function useDeleteSaleStatus() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteSaleStatus}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteSaleStatus = async (id: number) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteSaleStatus}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete sale status';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Sale status deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Error deleting sale status:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while deleting the sale status'
      );
      return false;
    }
  };

  return {
    deleteSaleStatus,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateSaleStatus() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateSaleStatus}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateSaleStatus = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateSaleStatus}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating sale status:', err);
      return [];
    }
  };

  return {
    updateSaleStatus,
    isLoading: !data && !error,
    error,
  };
}
export function useAddEmployeeStatus() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addEmployeeStatus,
    handlePostRequestWithArg
  );

  const addEmployeeStatus = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addEmployeeStatus,
    isLoading: isMutating,
    error,
  };
}
export function useDeleteEmployeeStatus() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteEmployeeStatus}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteEmployeeStatus = async (id: number) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteEmployeeStatus}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete employee status';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Employee Status deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Error deleting employee status:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while deleting the employee status'
      );
      return false;
    }
  };

  return {
    deleteEmployeeStatus,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateEmployeeStatus() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateEmployeeStatus}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );
  const updateEmployeeStatus = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateEmployeeStatus}?id=${id}`,
        requestData
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to update employee status';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Employee Status updated successfully');
      return true;
    } catch (err: any) {
      console.error('Error updating employee status:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while updating the employee status'
      );
      return false;
    }
  };

  return {
    updateEmployeeStatus,
    isLoading: !data && !error,
    error,
  };
}
export function useAddContractType() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addContractType,
    handlePostRequestWithArg
  );

  const addContractType = async (requestData: any) => {
    console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addContractType,
    isLoading: isMutating,
    error,
  };
}
export function useDeleteContractType() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteContractType}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteContractType = async (id: number) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteContractType}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete contract';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Contract deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Error deleting contract:', err);
      toast.error(err?.returnMessage || 'An unexpected error occurred while deleting the contract');
      return false;
    }
  };

  return {
    deleteContractType,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateContractType() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateContractType}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateContractType = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateContractType}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating contract type:', err);
      return [];
    }
  };

  return {
    updateContractType,
    isLoading: !data && !error,
    error,
  };
}
export function useAddExternalLinks() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addExternalLink,
    handlePostRequestWithArg
  );

  const addExternalLink = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    try {
      const response = await trigger(requestData);
      // console.log('Response:', response);
      return response;
    } catch (err) {
      console.error('Error occurred:', err);
      return { error: err }; // Return an object containing the error
    }
  };

  return {
    addExternalLink,
    isLoading: isMutating,
    error,
  };
}

export function useDeleteExternalLink() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteExternalLink}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteExternalLink = async (id: number) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteExternalLink}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete external links';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('External Link deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Error deleting external link:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while deleting the external link'
      );
      return false;
    }
  };

  return {
    deleteExternalLink,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateExternalLink() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateExternalLink}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateExternalLink = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateExternalLink}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating external link:', err);
      return [];
    }
  };

  return {
    updateExternalLink,
    isLoading: !data && !error,
    error,
  };
}
export function useAddProjectGoals() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addProjectGoals,
    handlePostRequestWithArg
  );

  const addProjectGoals = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addProjectGoals,
    isLoading: isMutating,
    error,
  };
}
export function useDeleteProjectGoals() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteProjectGoals}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteProjectGoals = async (id: number) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteProjectGoals}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete project goals';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Project Goal deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Error deleting project goal:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while deleting the project goal'
      );
      return false;
    }
  };

  return {
    deleteProjectGoals,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateProjectGoals() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateProjectGoals}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateProjectGoals = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateProjectGoals}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating project goals:', err);
      return [];
    }
  };

  return {
    updateProjectGoals,
    isLoading: !data && !error,
    error,
  };
}
export function useAddCurrency() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addCurrency,
    handlePostRequestWithArg
  );

  const addCurrency = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addCurrency,
    isLoading: isMutating,
    error,
  };
}
export function useAddDesignation() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addDesignation,
    handlePostRequestWithArg
  );

  const addDesignation = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addDesignation,
    isLoading: isMutating,
    error,
  };
}
export function useDeleteDesignation() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteDesignation}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteDesignation = async (id: number) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteDesignation}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete designation';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Designation deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Error deleting designation:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while deleting the designation'
      );
      return false;
    }
  };

  return {
    deleteDesignation,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateDesignation() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateDesignation}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateDesignation = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateDesignation}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating designation:', err);
      return [];
    }
  };

  return {
    updateDesignation,
    isLoading: !data && !error,
    error,
  };
}
export function useGetDepartmentHierarchyWithChild() {
  const { data, error, mutate } = useSWR(
    (searchText: string, pageNo: number, pageSize: number) =>
      searchText !== undefined && pageNo !== undefined && pageSize !== undefined
        ? `${apiEndpoints.setup.getDeptHierarchyWithChild}?SearchText=${searchText}&PageNo=${pageNo}&PageSize=${pageSize}`
        : null,
    handleGetRequestWithArg,
    swrOptions
  );

  const getDepartmentHierarchyWithChild = async (
    searchText: string,
    pageNo: number = 1,
    pageSize: number = 12
  ) => {
    if (searchText === undefined || pageNo === undefined || pageSize === undefined) {
      console.error('Invalid parameters:', { searchText, pageNo, pageSize });
      return [];
    }
    try {
      const queryUrl = `${apiEndpoints.setup.getDeptHierarchyWithChild}?SearchText=${searchText}&PageNo=${pageNo}&PageSize=${pageSize}`;
      const response = await handleGetRequestWithArg(queryUrl, {});
      const result = await response;

      mutate();
      return result;
    } catch (err) {
      console.error('Error fetching Heirarchy Data:', err);
      return [];
    }
  };

  return {
    getDepartmentHierarchyWithChild,
    isLoading: !data && !error,
  };
}
export function useAddDepartment() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addDepartment,
    handlePostRequestWithArg
  );

  const addDepartment = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addDepartment,
    isLoading: isMutating,
    error,
  };
}
export function useDeleteDepartment() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteDepartment}?id=${id}` : null),
    handleDeleteRequestWithArg,
    swrOptions
  );

  const deleteDepartment = async (id: number) => {
    if (id === undefined || id === null) {
      return;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteDepartment}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete department';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Department deleted successfully');
    } catch (err: any) {
      console.error('Error deleting department:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while deleting the department'
      );
    }
  };

  return {
    deleteDepartment,
    isLoading: !data && !error,
    error,
  };
}
export function useUpdateDepartment() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateDepartment}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateDepartment = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateDepartment}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating department:', err);
      return [];
    }
  };

  return {
    updateDepartment,
    isLoading: !data && !error,
    error,
  };
}
export function useAddLoyaltyPoints() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addLoyaltyPoints,
    handlePostRequestWithArg
  );

  const addLoyaltyPoints = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addLoyaltyPoints,
    isLoading: isMutating,
    error,
  };
}
export function useGetSettingsFilter() {
  const requestData = {
    sort: [{ direction: 'desc', name: 'id' }],
    page: 1,
    pageSize: 1000,

    filter: {
      name: 'name',
      operator: 'eq',
      value: 'RewardPointMultiplier',
      logic: 'and',
    },
  };

  const { data, isLoading, error, isValidating } = useSWR(
    [apiEndpoints.setup.settingsFilter, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const loyaltyWeights = data?.data ?? [];

    return {
      data: { loyaltyWeights },
      loyaltyWeightsLoading: isLoading,
      loyaltyWeightsError: error,
      loyaltyWeightsValidating: isValidating,
      loyaltyWeightsEmpty: !isLoading && !loyaltyWeights.length,
      mutateLoyaltyWeightsList: () => mutate([apiEndpoints.setup.settingsFilter, requestData]),
    };
  }, [data?.data, data?.total, error, isLoading, isValidating]);

  return memoizedValue;
}
export function useGetCompanyInfo() {
  const GET_COMPANY_INFO = `${apiEndpoints.setup.getCompanyInfo}`;

  const { data, error, isLoading, isValidating } = useSWR(GET_COMPANY_INFO, fetcher, swrOptions);

  const memoizedValue = useMemo(() => {
    const companyInfo = data?.data ?? null;
    const hasError = Boolean(error);

    return {
      companyInfo,
      companyInfoLoading: isLoading,
      companyInfoError: hasError ? error : null,
      companyInfoValidating: isValidating,
      companyInfoNotFound: !isLoading && !companyInfo,
      mutateCompanyInfo: () => mutate(GET_COMPANY_INFO),
    };
  }, [data?.data, error, isLoading, isValidating]);

  return memoizedValue;
}

export function useUpdateCompanyInfo() {
  const { data, error } = useSWR(null, handlePutRequestWithArg, swrOptions);

  const updateCompanyInfo = useCallback(async (requestData: any) => {
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateCompanyInfo}`,
        requestData
      );

      mutate(`${apiEndpoints.setup.getCompanyInfo}`);

      return response;
    } catch (err) {
      console.error('Error updating company info:', err);
      return [];
    }
  }, []);

  return {
    updateCompanyInfo, // Stable reference
    isLoading: !data && !error,
    error,
    mutateCompanyInfo: useCallback(() => mutate(`${apiEndpoints.setup.getCompanyInfo}`), []),
  };
}

export function useGetEmployeesStatus() {
  const GET_EMPLOYEE_STATUS = apiEndpoints.setup.getAllEmployeeStatus;

  const { data, error, isLoading, isValidating } = useSWR(GET_EMPLOYEE_STATUS, fetcher, swrOptions);

  const memoizedValue = useMemo(() => {
    const employeeStatus = data?.data ?? null;
    const hasError = Boolean(error);

    return {
      employeeStatus,
      employeeStatusLoading: isLoading,
      employeeStatusError: hasError ? error : null,
      employeeStatusValidating: isValidating,
      companyInfoNotFound: !isLoading && !employeeStatus,
      mutateEmployee: () => mutate(GET_EMPLOYEE_STATUS),
    };
  }, [data?.data, error, isLoading, isValidating]);

  return memoizedValue;
}
export async function changeEmployeeStatus(data: any) {
  try {
    const requestData = {
      description: data.description,
      employeeId: data.employeeId,
      employeeStatusId: data.employeeStatusId,
      name: data.name || '',
      isActive: data.isActive,
      workStartTime: data.workStartTime,
      workEndTime: data.workEndTime,
    };

    const response = await tairraAxiosInstance.post(
      apiEndpoints.dashboard.insertEmployeeStatus,
      requestData
    );

    if (response?.data?.returnStatus === true) {
      const newStatus = response.data.data;

      return {
        success: true,
        data: newStatus,
        // mutateTeamList: () => mutate([apiEndpoints.employee.filterAssigned]),
      };
    } else {
      throw new Error('Failed to update status');
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.errorMessage || 'Something went wrong!';
    const errorCodes = error.response?.data?.errorsCodes || [];
    const statusCode = error.response?.data?.statusCode || null;

    return {
      success: false,
      error: {
        message: errorMessage,
        codes: errorCodes,
        statusCode: statusCode,
      },
    };
  }
}
export function useGetAll(id: number) {
  let GET_EMPLOYEE_BY_ID = `${apiEndpoints.employee.getById}/${id}`;
  const { data, error, isValidating } = useSWR(
    id !== undefined ? GET_EMPLOYEE_BY_ID : null,
    fetcher,
    swrOptions
  );

  const memoizedEmployee = useMemo(() => {
    const employeeDetails = data?.data ?? [];

    const hasError = Boolean(error);

    return {
      employeeDetails,
      employeeLoading: !data && !error,
      employeeError: hasError ? error : null,
      employeeValidating: isValidating,
      employeeNotFound: !data?.data?.length && !error,
      mutateEmployee: () => mutate(GET_EMPLOYEE_BY_ID),
    };
  }, [data?.data, error, isValidating, id]);

  return memoizedEmployee;
}
export function useUpdateEmployee() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.dashboard.updateProfile}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );
  const updateEmployeeProfile = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return false;
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.dashboard.updateProfile}?id=${id}`,
        requestData
      );
      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to update profile ';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Profile updated successfully');
      return true;
    } catch (err: any) {
      console.error('Error updating employee:', err);
      toast.error(err?.returnMessage || 'An unexpected error occurred while updating the profile');
      return false;
    }
  };

  return {
    updateEmployeeProfile,
    isLoading: !data && !error,
    error,
  };
}
export async function changePassword(data: any) {
  try {
    const requestData = {
      currentPassword: data.currentPassword,
      updatedPassword: data.updatedPassword,
    };

    const response = await tairraAxiosInstance.post(
      apiEndpoints.dashboard.changeProfile,
      requestData
    );

    if (response?.data?.returnStatus === true) {
      // console.log('This is the response from avctions', response.data.returnMessage[0]);

      toast.success(response.data.returnMessage[0]);
      return {
        success: true,
      };
    } else {
      // console.log('This is the response from avctions', response.data.returnMessage[0]);
      toast.error(response.data.returnMessage[0]);
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.errorMessage || 'Something went wrong!';
    const errorCodes = error.response?.data?.errorsCodes || [];
    const statusCode = error.response?.data?.statusCode || null;

    return {
      success: false,
      error: {
        message: errorMessage,
        codes: errorCodes,
        statusCode: statusCode,
      },
    };
  }
}
export function useGetNotifications(employeeId: number) {
  const requestData = {
    employeeId: employeeId,
    isClearAll: false,
  };

  const { data, error, isValidating, mutate } = useSWR(
    [apiEndpoints.setup.getNotifications, requestData],
    postFetcher,
    swrOptions
  );

  const memoizedValue = useMemo(() => {
    const notifications = data?.data ?? [];
    const total = data?.total ?? 0;

    return {
      notificationList: { notifications, total },
      isLoading: !data && !error,
      error,
      isValidating,
      isEmpty: !data && !error && !notifications.length,
      mutateNotifications: () => mutate([apiEndpoints.setup.getNotifications, requestData]),
    };
  }, [data, error, isValidating, employeeId]);

  return memoizedValue;
}
export async function CreateDeptLevels(
  level1: string,
  level2: string,
  level3: string,
  level4: string
) {
  try {
    const requestData = [
      {
        name: level1,
        level: 0,
      },
      {
        name: level2,
        level: 1,
      },
      {
        name: level3,
        level: 2,
      },
      {
        name: level4,
        level: 3,
      },
    ];

    const response = await tairraAxiosInstance.post(
      apiEndpoints.setup.createDepartmentLevels,
      requestData
    );

    if (response?.data?.returnStatus === true) {
      const newLevels = response.data.data;

      // toast.success('Department Levels Created successfully!', { position: 'top-center' });
      return { success: true, data: newLevels };
    } else {
      throw new Error('Failed to create Department Levels');
    }
  } catch (error: any) {
    console.error('Error creating Department Levels:', error);

    // Check if the error response contains specific error messages
    const errorMessage = error.returnMessage || ['Something went wrong!'];
    toast.error(
      'Error creating Department Levels: ' + (errorMessage.join(', ') || 'Something went wrong!'),
      {
        position: 'top-center',
      }
    );

    return {
      success: false,
      error: {
        message: errorMessage,
        codes: error.response?.data?.errorsCodes || [],
        statusCode: error.response?.data?.statusCode || null,
      },
    };
  }
}
export function useUpdateLevels() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateLevels}?id=${id}` : null),
    handlePutRequestWithArg,
    swrOptions
  );

  const updateDeptLevels = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateLevels}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating employee:', err);
      return [];
    }
  };

  return {
    updateDeptLevels,
    isLoading: !data && !error,
    error,
  };
}
export async function addTeamNotice(teamNotice: string) {
  try {
    const requestData = { teamNotice };

    const response = await tairraAxiosInstance.post(apiEndpoints.setup.addTeamNotice, requestData);

    // console.log('This is the response', response);
    if (response?.data?.returnStatus) {
      if (response?.data?.data?.teamNotice === '') {
        toast.success('Team Notice Cleared Successfully');
      } else {
        toast.success('Team Notice Added Successfully');
      }
      return { success: true, response };
    } else {
      return { success: false };
    }
  } catch (error: any) {
    const errorMessage = error?.returnMessage || ['Something went wrong!'];
    toast.error(errorMessage[0]);
    console.error('Error :', error);
    return { success: false };
  }
}

export function useAddSkills() {
  const { trigger, isMutating, error } = useSWRMutation(
    apiEndpoints.setup.addSkills,
    handlePostRequestWithArg
  );

  const addSkills = async (requestData: any) => {
    // console.log('Request Data:', JSON.stringify(requestData));

    await trigger(requestData);
  };

  return {
    addSkills,
    isLoading: isMutating,
    error,
  };
}

export function useDeleteSkills() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.deleteSkill}?id=${id}` : null),
    handleDeleteRequestWithArg
  );

  const deleteSkills = async (id: number) => {
    if (id === undefined || id === null) {
      return false;
    }

    try {
      const response = await handleDeleteRequestWithArg(
        `${apiEndpoints.setup.deleteSkill}?id=${id}`,
        {}
      );

      if (!response?.returnStatus) {
        const errorMessage = response?.returnMessage?.[0] || 'Failed to delete skill';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }

      toast.success('Skill deleted successfully');
      return true;
    } catch (err: any) {
      console.error('Error deleting skill:', err);
      toast.error(
        err?.returnMessage || 'An unexpected error occurred while deleting the project goal'
      );
      return false;
    }
  };

  return {
    deleteSkills,
    isLoading: !data && !error,
    error,
  };
}

export function useUpdateSkills() {
  const { data, error } = useSWR(
    (id: number) => (id ? `${apiEndpoints.setup.updateSkills}?id=${id}` : null),
    handlePutRequestWithArg
  );

  const updateSkills = async (id: number, requestData: any) => {
    if (id === undefined || id === null) {
      return [];
    }
    try {
      const response = await handlePutRequestWithArg(
        `${apiEndpoints.setup.updateSkills}?id=${id}`,
        requestData
      );
      const result = await response;
      return result;
    } catch (err) {
      console.error('Error updating skills:', err);
      return [];
    }
  };

  return {
    updateSkills,
    isLoading: !data && !error,
    error,
  };
}

export async function markAsSeen(
  itemId: number,
  type: number,
  employeeId: number,
  projectId?: number
) {
  try {
    const response = await tairraAxiosInstance.post(apiEndpoints.notifications.clear, {
      itemId: itemId,
      type: type,
      employeeId: employeeId,
      isClearAll: false,
      projectId: projectId,
    });

    if (response?.data?.returnStatus === true) {
      return { success: true };
    } else {
      throw new Error('Failed to mark notification as seen.');
    }
  } catch (error: any) {
    console.error('Error marking notification as seen:', error);
    return {
      success: false,
      error: {
        message: error.response?.data?.returnMessage || 'Error marking notification as seen.',
        codes: error.response?.data?.errorsCodes || [],
        statusCode: error.response?.data?.statusCode || null,
      },
    };
  }
}

export async function clearAllNotifications(employeeId: number) {
  try {
    const response = await tairraAxiosInstance.post(apiEndpoints.notifications.clear, {
      employeeId: employeeId,
      isClearAll: true,
    });

    if (response?.data?.returnStatus === true) {
      return { success: true };
    } else {
      throw new Error('Failed to clear notification.');
    }
  } catch (error: any) {
    console.error('Error clearing notification:', error);
    return {
      success: false,
      error: {
        message: error.response?.data?.returnMessage || 'Error clearing notification.',
        codes: error.response?.data?.errorsCodes || [],
        statusCode: error.response?.data?.statusCode || null,
      },
    };
  }
}
